import { useCallback, useEffect, useMemo, useState } from 'react';

import * as SituacaoAPI from '../../../../api/situacao';
import CrudsAPI from '../../../../api/CRUDs';

import { groupByPessoa } from '../utils/groupByPessoa';

export function useHomologationPessoaModule({
    dados,
    setFlashMessage,
    parametro,
    pessoaId,
    empresaId,
    empresaIds,
    empresaTabelaId,
    mrt,
    isAtive,
}) {
    const [rows, setRows] = useState([]);
    const [selectedRow, setSelectedRow] = useState(null);

    const [openDialog, setOpenDialog] = useState(false);
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);

    const [draft, setDraft] = useState({});

    const [optionsPapel, setOptionsPapel] = useState([]);
    const [optionsPapelFilter, setOptionsPapelFilter] = useState([]);

    const {
        globalFilter,
        pagination,

        isError,
        isLoading,
        isRefetching,
        setIsError,
        setIsLoading,
        setIsRefetching,
        rowCount,
        setRowCount,
    } = mrt;

    const carregarOptionsIniciais = useCallback(async () => {
        try {
            const situacoes = await SituacaoAPI.situacoes(empresaTabelaId);
            const papeis = (situacoes || []).filter((item) => item.homologationPessoa === true);

            const mappedPapeis = papeis.map((item) => ({
                text: item.descricao,
                value: item.descricao,
            }));

            setOptionsPapel(papeis);
            setOptionsPapelFilter(mappedPapeis);
        } catch (error) {
            console.error(error);
        }
    }, [empresaTabelaId]);

    const fetchData = useCallback(async () => {
        if (!dados?.id) return;

        if (!rows.length) setIsLoading(true);
        else setIsRefetching(true);

        try {
            const filtroHomologacao = [{ id: 'homologationId', value: dados?.id }];
            const filtrosModeEquals = JSON.stringify({ homologationId: 'equals' });

            const [respPessoas, respRegistros, respItens] = await Promise.all([
                CrudsAPI.homologationPessoa.listarObj({
                    start: 0,
                    size: 9999,
                    filters: JSON.stringify(filtroHomologacao),
                    filtersMode: filtrosModeEquals,
                    globalFilter: '',
                    sorting: JSON.stringify([]),
                    homologationId: dados?.id,
                }),
                CrudsAPI.homologationRegistro.listarObj({
                    start: 0,
                    size: 9999,
                    filters: JSON.stringify([]),
                    filtersMode: JSON.stringify({}),
                    globalFilter: '',
                    sorting: JSON.stringify([]),
                    homologationId: dados?.id,
                }),
                CrudsAPI.homologationItem.listarObj({
                    start: 0,
                    size: 9999,
                    filters: JSON.stringify(filtroHomologacao),
                    filtersMode: filtrosModeEquals,
                    globalFilter: '',
                    sorting: JSON.stringify([]),
                    homologationId: dados?.id,
                }),
            ]);

            const listaPessoas = respPessoas?.data?.data || [];

            const isResponsavelHomologacao = listaPessoas.some((p) => {
                const mesmaPessoa = String(p?.pessoaId) === String(pessoaId);
                const papelResponsavel = String(p?.situacaoId) === String(parametro?.situacaoHomologacaoPessoaId);
                const vinculoHomologacao = !p?.itemId;

                return mesmaPessoa && papelResponsavel && vinculoHomologacao;
            });

            const listaItens = respItens?.data?.data || [];
            const listaRegistrosBruta = respRegistros?.data?.data || [];

            const listaRegistros = listaRegistrosBruta.filter(
                (r) => r?.homologationItem?.homologationId === dados.id || r?.itemId === dados.id
            );

            let consolidado = groupByPessoa({
                pessoas: listaPessoas,
                registros: listaRegistros,
                itens: listaItens,
            });

            if (!isResponsavelHomologacao) {
                consolidado = consolidado.filter((r) => String(r.pessoaId) === String(pessoaId));
            }

            if (globalFilter) {
                const termo = String(globalFilter).toLowerCase();

                consolidado = consolidado.filter((row) => {
                    return (
                        String(row?.pessoa || '')
                            .toLowerCase()
                            .includes(termo) ||
                        String(row?.papelPrincipal || '')
                            .toLowerCase()
                            .includes(termo) ||
                        String(row?.status || '')
                            .toLowerCase()
                            .includes(termo)
                    );
                });
            }

            const start = pagination.pageIndex * pagination.pageSize;
            const end = start + pagination.pageSize;
            const pagina = consolidado.slice(start, end);

            setRows(pagina);
            setRowCount(consolidado.length);
            setIsError(false);
        } catch (error) {
            console.error(error);
            setIsError(true);
        } finally {
            setIsLoading(false);
            setIsRefetching(false);
        }
    }, [
        dados?.id,
        rows.length,
        pagination.pageIndex,
        pagination.pageSize,
        globalFilter,
        pessoaId,
        parametro?.situacaoHomologacaoPessoaId,
        setIsError,
        setIsLoading,
        setIsRefetching,
        setRowCount,
    ]);

    const onEnterTab = useCallback(async () => {
        if (!dados?.id) return;

        await carregarOptionsIniciais();
        await fetchData();
    }, [dados?.id, carregarOptionsIniciais, fetchData]);

    useEffect(() => {
        if (!isAtive) return;
        onEnterTab();
    }, [isAtive, onEnterTab]);

    const handleAdicionar = useCallback(() => {
        setDraft({
            ativo: true,
            homologationId: dados.id,
            itemId: null,
            dataEntrada: new Date().toISOString().substring(0, 10),
        });

        setSelectedRow(null);
        setOpenDialog(true);
    }, [dados?.id]);

    const handleEditar = useCallback(
        (row) => {
            const obj = row.original;

            setSelectedRow(obj);

            setDraft({
                ...obj,
                homologationId: obj.homologationId || dados.id,
                itemId: null,
                dataEntrada: obj.dataEntrada ? String(obj.dataEntrada).substring(0, 10) : '',
                dataSaida: obj.dataSaida ? String(obj.dataSaida).substring(0, 10) : '',
            });

            setOpenDialog(true);
        },
        [dados?.id]
    );

    const handleSalvarDraft = useCallback(
        async (obj) => {
            try {
                let response;

                const payload = {
                    ...obj,
                    homologationId: dados.id,
                    itemId: null,
                };

                if (payload.uuid) response = await CrudsAPI.homologationPessoa.editar(payload);
                else response = await CrudsAPI.homologationPessoa.cadastrar(payload);

                setFlashMessage(response.data.message, response.data.type);

                setOpenDialog(false);
                setDraft({});
                setSelectedRow(null);

                await fetchData();
            } catch (error) {
                console.error(error);
                setFlashMessage(error?.response?.data?.message || 'Erro ao salvar participante!', 'error');
            }
        },
        [dados?.id, fetchData, setFlashMessage]
    );

    const handleDeletar = useCallback(async () => {
        try {
            if (!selectedRow?.uuid) return;

            const { data } = await CrudsAPI.homologationPessoa.deletar({ id: selectedRow.uuid });

            setFlashMessage(data.message, data.type);

            setOpenDeleteDialog(false);
            setSelectedRow(null);

            await fetchData();
        } catch (error) {
            console.error(error);
            setFlashMessage(error?.response?.data?.message || 'Erro ao deletar participante!', 'error');
        }
    }, [selectedRow, fetchData, setFlashMessage]);

    const handleCancelar = useCallback(() => {
        setOpenDialog(false);
        setOpenDeleteDialog(false);
        setSelectedRow(null);
        setDraft({});
    }, []);

    return useMemo(
        () => ({
            state: {
                rows,
                selectedRow,
            },

            actions: {
                fetchData,
                onEnterTab,
                handleAdicionar,
                handleEditar,
                handleSalvarDraft,
                handleDeletar,
                handleCancelar,
                carregarOptionsIniciais,
                setSelectedRow,
                setOpenDeleteDialog,
            },

            dialogs: {
                openDialog,
                setOpenDialog,
                openDeleteDialog,
                setOpenDeleteDialog,
                draft,
                setDraft,
            },

            mrt: {
                ...mrt,
                isError,
                isLoading,
                isRefetching,
                rowCount,
            },

            options: {
                optionsPapel,
                optionsPapelFilter,
            },

            context: {
                pessoaId,
                empresaId,
                empresaIds,
                empresaTabelaId,
            },
        }),
        [
            rows,
            selectedRow,
            fetchData,
            onEnterTab,
            handleAdicionar,
            handleEditar,
            handleSalvarDraft,
            handleDeletar,
            handleCancelar,
            carregarOptionsIniciais,
            openDialog,
            openDeleteDialog,
            draft,
            mrt,
            isError,
            isLoading,
            isRefetching,
            rowCount,
            optionsPapel,
            optionsPapelFilter,
            pessoaId,
            empresaId,
            empresaIds,
            empresaTabelaId,
        ]
    );
}
